"use client";

import React from "react";

import PatientPaymentSurface from "@/components/payments/patient-payment-surface";
import RainforestSkeleton from "@/components/rainforest/rainforest-skeleton";
import { PayinSessionData } from "@/components/rainforest/use-rainforest-payin-session";

import { BinLookupResult } from "@/types/rainforest";

interface PayinSessionSurfaceProps {
  session: PayinSessionData | null;
  onSuccess: (reference: string) => void;
  onFailure: (err: unknown) => void;
  onBinLookup?: (result: BinLookupResult) => void;
  className?: string;
  allowApplePay?: boolean;
  allowedMethods?: string;
  skeletonClassName?: string;
}

/**
 * The loading skeleton until a payin session is ready, then the card form.
 *
 * Shared by the self-schedule booking-fee checkout and the online gift-card
 * purchase. It used to render `<RainforestPaymentLink>` directly, which on a
 * Stripe merchant handed a Stripe client secret to Rainforest's SDK: a patient
 * looking at a card field that never works, on a page they were sent to in
 * order to pay. `PatientPaymentSurface` reads the session's own `processor` and
 * mounts whichever form belongs to it.
 */
const PayinSessionSurface: React.FC<PayinSessionSurfaceProps> = ({
  session,
  onSuccess,
  onFailure,
  onBinLookup,
  className,
  allowApplePay = true,
  allowedMethods,
  skeletonClassName,
}) => {
  if (!session) {
    return (
      <RainforestSkeleton
        className={skeletonClassName ?? "motion-preset-slide-right-sm"}
      />
    );
  }

  return (
    <PatientPaymentSurface
      processor={session.processor}
      onSuccess={onSuccess}
      onFailure={onFailure}
      onBinLookup={onBinLookup}
      sessionKey={session.sessionKey}
      payinConfigId={session.payinConfigId}
      sandbox={session.sandbox}
      publishableKey={session.publishableKey}
      stripeAccount={session.stripeAccount}
      clientSecret={session.clientSecret}
      allowApplePay={allowApplePay}
      allowedMethods={allowedMethods}
      className={className}
    />
  );
};

export default PayinSessionSurface;
